import { Box } from '@mui/material';

import type { KeyboardEvent, RefObject } from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import type { SpotHistoryTableColumnType } from '@/types/markets';

import ColumnHeaderCell from './ColumnHeaderCell';
import type { VirtualCell } from './types';
import {
  createResizeDoubleClickHandler,
  createResizeKeyDownHandler,
  createResizePointerHandler
} from './useColumnResize';
import type { SetColumnWidth } from './useColumnWidths';
import { getColumnCode } from './utils';

function getElementHeight(element: HTMLElement) {
  const styles = window.getComputedStyle(element);
  const parseStyleValue = (value: string) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const borderSpacing =
    parseStyleValue(styles.borderTopWidth) + parseStyleValue(styles.borderBottomWidth);

  // Temporarily clear the inline height so percentage-based children (height: 100%)
  // collapse to their natural content size, giving a stable measurement that doesn't
  // feed back into the height-equalisation loop.
  const savedHeight = element.style.height;
  element.style.height = '';
  const height = Math.ceil(
    Math.max(
      element.scrollHeight + borderSpacing,
      element.offsetHeight,
      element.getBoundingClientRect().height
    )
  );
  element.style.height = savedHeight;
  return height;
}

function measureHeaderHeight(rowElement: HTMLElement) {
  const headerCells = Array.from(rowElement.querySelectorAll<HTMLElement>('[role="columnheader"]'));

  return Math.max(0, ...headerCells.map(getElementHeight));
}

type Props = {
  columns: SpotHistoryTableColumnType[];
  virtualColumns: VirtualCell[];
  dataColumnWidths: number[];
  totalWidth: number;
  isHeaderExpanded: boolean;
  scrollRef: RefObject<HTMLDivElement | null>;
  setColumnWidth: SetColumnWidth;
  onToggle: () => void;
  onToggleKeyDown: (event: KeyboardEvent) => void;
  onResizeEnd: (shouldSuppressClick: boolean) => void;
  onHeightChange: (height: number) => void;
};

const TableHeaderRow = ({
  columns,
  virtualColumns,
  dataColumnWidths,
  totalWidth,
  isHeaderExpanded,
  scrollRef,
  setColumnWidth,
  onToggle,
  onToggleKeyDown,
  onResizeEnd,
  onHeightChange
}: Props) => {
  const resizeDeps = { scrollRef, totalWidth, setColumnWidth, onResizeEnd };
  const rowRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const measurementKey = virtualColumns.map(({ index, size }) => `${index}:${size}`).join('|');

  const updateHeaderHeight = useCallback(() => {
    const el = rowRef.current;
    if (!el) return;

    const nextHeight = measureHeaderHeight(el);
    setHeaderHeight(currentHeight => (currentHeight === nextHeight ? currentHeight : nextHeight));
    onHeightChange(nextHeight);
  }, [onHeightChange]);

  useLayoutEffect(() => {
    const el = rowRef.current;
    if (!el) return;

    updateHeaderHeight();

    if (typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver(() => {
      updateHeaderHeight();
    });
    el.querySelectorAll<HTMLElement>('[role="columnheader"], [data-header-cell-content]').forEach(
      cell => {
        ro.observe(cell);
      }
    );
    return () => {
      ro.disconnect();
    };
  }, [isHeaderExpanded, measurementKey, updateHeaderHeight]);

  return (
    <Box
      ref={rowRef}
      role='row'
      sx={{
        backgroundColor: 'background.paper',
        height: headerHeight || undefined,
        position: 'sticky',
        display: 'flex',
        top: 0,
        width: totalWidth,
        zIndex: 3,
        mb: 1.5
      }}
    >
      {virtualColumns.map(virtualColumn => {
        const column = columns[virtualColumn.index - 1];
        const columnWidth = dataColumnWidths[virtualColumn.index - 1];
        const columnCode = getColumnCode(column);

        return (
          <ColumnHeaderCell
            key={columnCode || virtualColumn.index}
            column={column}
            columnIndex={virtualColumn.index}
            columnWidth={columnWidth}
            rowHeight={headerHeight}
            isHeaderExpanded={isHeaderExpanded}
            virtualStart={virtualColumn.start}
            virtualSize={virtualColumn.size}
            onToggle={onToggle}
            onToggleKeyDown={onToggleKeyDown}
            onResizePointerDown={createResizePointerHandler(resizeDeps, {
              column,
              currentWidth: columnWidth,
              columnOrder: virtualColumn.index
            })}
            onResizeKeyDown={createResizeKeyDownHandler(setColumnWidth, column, columnWidth)}
            onResizeDoubleClick={createResizeDoubleClickHandler(setColumnWidth, column)}
          />
        );
      })}
    </Box>
  );
};

export default TableHeaderRow;
